import { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../../../components/Auth/AuthProvider';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Collapse,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import {
  getFeatureFlags,
  deleteFeatureFlag,
  FeatureFlag,
} from '../services/featureFlagApi';
import { FeatureFlagForm } from './FeatureFlagForm';
import { UserManagement } from './UserManagement';

interface FeatureFlagListProps {
  onError: (error: string) => void;
}

export function FeatureFlagList({ onError }: Readonly<FeatureFlagListProps>) {
  const { actions } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const [expandedFlag, setExpandedFlag] = useState<string | null>(null);
  const [editingFlag, setEditingFlag] = useState<FeatureFlag | null>(null);

  const {
    data: flags,
    isLoading,
    error,
    isError,
  } = useQuery({
    queryKey: ['featureFlags'],
    queryFn: async () => {
      const token = await actions?.getAccessToken();
      if (!token) {
        return [];
      }
      return getFeatureFlags(token);
    },
  });

  // Handle query errors
  useEffect(() => {
    if (isError && error) {
      onError(error.message);
    }
  }, [isError, error, onError]);

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = await actions?.getAccessToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      return deleteFeatureFlag(id, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['featureFlags'] });
    },
    onError: (err) => onError(err.message),
  });

  const handleToggleExpand = (id: string) => {
    setExpandedFlag(expandedFlag === id ? null : id);
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this feature flag?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleEdit = (flag: FeatureFlag) => {
    setEditingFlag(flag);
  };

  const handleCancelEdit = () => {
    setEditingFlag(null);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!flags || flags.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography>No feature flags found.</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ mb: 3 }}>
      <List sx={{ p: 0 }}>
        {flags.map((flag) => (
          <Box key={flag.id}>
            {editingFlag && editingFlag.id === flag.id ? (
              <Paper sx={{ p: 3, m: 1, boxShadow: 'none' }}>
                <FeatureFlagForm
                  flag={flag}
                  onCancel={handleCancelEdit}
                  onError={onError}
                />
              </Paper>
            ) : (
              <ListItem
                divider
                secondaryAction={
                  <Box>
                    <IconButton onClick={() => handleEdit(flag)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(flag.id)}
                      disabled={deleteMutation.isPending}
                    >
                      <DeleteIcon />
                    </IconButton>
                    <IconButton onClick={() => handleToggleExpand(flag.id)}>
                      {expandedFlag === flag.id ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </Box>
                }
              >
                <ListItemText
                  primary={flag.name}
                  secondary={flag.description ?? 'No description'}
                />
              </ListItem>
            )}
            <Collapse in={expandedFlag === flag.id}>
              <Box
                sx={{
                  p: 3,
                  borderBottom:
                    expandedFlag === flag.id
                      ? '1px solid rgba(0, 0, 0, 0.12)'
                      : 'none',
                }}
              >
                <Typography variant='subtitle2' gutterBottom>
                  Active Identifiers: {flag.userIdentifiers.length}
                </Typography>
                <UserManagement
                  flagId={flag.id}
                  userIds={flag.userIdentifiers}
                  onError={onError}
                />
              </Box>
            </Collapse>
          </Box>
        ))}
      </List>
    </Paper>
  );
}
